/**
 * Одна строка уведомления. Общая для колокольчика и истории.
 *
 * Общая намеренно: два разных представления одного уведомления
 * разъехались бы при первой же новой категории, и человек читал бы
 * в колокольчике одно, а в истории — другое.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/shared/ui";

import type { NotificationItem } from "@taskflow/client-core/notifications";
import { CategoryIcon } from "./CategoryIcon";
import { actorTextOf, headlineOf, linkOf, summaryOf } from "./notificationText";

interface NotificationRowProps {
  item: NotificationItem;
  /** Вызывается при переходе по ссылке: строку пора считать прочитанной. */
  onOpen: (item: NotificationItem) => void;
  /** Кнопка справа — «вернуть в непрочитанные» в истории. */
  trailing?: ReactNode;
  compact?: boolean;
}

export function NotificationRow({ item, onOpen, trailing, compact = false }: NotificationRowProps) {
  const actor = actorTextOf(item);

  return (
    <li
      className={cn(
        "relative flex gap-3 border-b border-line/60 last:border-b-0",
        compact ? "px-3 py-2.5" : "px-4 py-3",
        // Непрочитанное выделено фоном, а не только точкой: точка
        // диаметром в четыре пикселя не читается боковым зрением,
        // и список выглядит однородным независимо от состояния.
        item.read ? "bg-transparent" : "bg-accent-soft/40",
      )}
    >
      <CategoryIcon type={item.type} className="mt-0.5 size-4 shrink-0 text-muted" />

      <div className="min-w-0 flex-1">
        <Link
          to={linkOf(item)}
          onClick={() => onOpen(item)}
          // Растянутая ссылка: нажимается вся строка, а не подчёркнутый
          // текст. Кнопки справа лежат выше по z-порядку и перехватывают
          // своё нажатие сами.
          className="after:absolute after:inset-0 focus-visible:outline-2 focus-visible:outline-accent"
        >
          <span className="block truncate text-[13px] font-semibold text-fg">
            {headlineOf(item)}
          </span>
        </Link>

        <p className="mt-0.5 line-clamp-2 text-[13px] text-muted">{summaryOf(item)}</p>

        {item.payload.changes && item.payload.changes.length > 0 && (
          <ul className="mt-1 flex flex-col gap-0.5">
            {item.payload.changes.map((change) => (
              <li key={change.field} className="truncate text-[12px] text-muted">
                <span className="text-faint">{change.field}: </span>
                <span className="line-through decoration-faint">
                  {change.before ?? "не задано"}
                </span>
                {" → "}
                <span className="font-semibold text-fg">{change.after ?? "не задано"}</span>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[12px] text-faint">
          {actor !== null && <span>{actor}</span>}
          <span>{item.createdAtLabel}</span>
          {item.count > 1 && (
            <span className="rounded-full bg-sunken px-1.5 py-px font-semibold text-muted">
              {item.count} изменения
            </span>
          )}
          {item.payload.boardName !== null && item.payload.boardName !== undefined && (
            <span className="truncate">{item.payload.boardName}</span>
          )}
        </p>
      </div>

      {trailing !== undefined && <div className="relative z-10 shrink-0">{trailing}</div>}
    </li>
  );
}
